import createGuid from 'src/utils/guid';
import {
  DESTINO_RAMIFICACAO,
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  normalizeFormularioGlobalQuestionType,
} from './FormularioGlobalQuestionTypes';

const CLIENT_ID_FIELD = '_formularioGlobalClientId';

export const BRANCHING_TARGET_TYPE = {
  QUESTION: 'pergunta',
  SECTION: 'secao',
};

export const BRANCHING_FINISH_VALUE = 'concluir';

export const canConfigureQuestionBranching = (tipo) =>
  normalizeFormularioGlobalQuestionType(tipo) ===
  FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA;

export const isQuestionBranchingEnabled = (question) =>
  canConfigureQuestionBranching(question.tipo || question.tipoPergunta) &&
  !!(
    question.aplicarRamificacao ??
    (question.opcoes || []).some((option) => !!option.destinoRamificacao)
  );

export const getBranchingGuidKey = (guid) =>
  String(guid || '')
    .trim()
    .toLowerCase();

export const getBranchingQuestionKey = (question) =>
  question[CLIENT_ID_FIELD] ||
  getBranchingGuidKey(question.guid) ||
  (question.id > 0 ? `question-id-${question.id}` : '');

export const getBranchingTargetValue = (type, guid) =>
  guid ? `${type}:${getBranchingGuidKey(guid)}` : '';

// Valor do select de destino: "pergunta:<guid>", "secao:<guid>" ou "concluir"
export const getBranchingDestinationValue = (option) => {
  switch (Number(option?.destinoRamificacao)) {
    case DESTINO_RAMIFICACAO.PERGUNTA:
      return getBranchingTargetValue(
        BRANCHING_TARGET_TYPE.QUESTION,
        option.destinoRamificacaoGuid
      );
    case DESTINO_RAMIFICACAO.SECAO:
      return getBranchingTargetValue(
        BRANCHING_TARGET_TYPE.SECTION,
        option.destinoRamificacaoGuid
      );
    case DESTINO_RAMIFICACAO.CONCLUIR_FORMULARIO:
      return BRANCHING_FINISH_VALUE;
    default:
      return '';
  }
};

export const parseBranchingDestinationValue = (value) => {
  if (value === BRANCHING_FINISH_VALUE) {
    return {
      destinoRamificacao: DESTINO_RAMIFICACAO.CONCLUIR_FORMULARIO,
      destinoRamificacaoGuid: null,
    };
  }

  const [type, guid] = String(value || '').split(':');
  const destinoRamificacao =
    type === BRANCHING_TARGET_TYPE.QUESTION
      ? DESTINO_RAMIFICACAO.PERGUNTA
      : type === BRANCHING_TARGET_TYPE.SECTION
      ? DESTINO_RAMIFICACAO.SECAO
      : null;

  return destinoRamificacao && guid
    ? { destinoRamificacao, destinoRamificacaoGuid: guid }
    : { destinoRamificacao: null, destinoRamificacaoGuid: null };
};

const getOrderedRootItems = (formulario) =>
  [...(formulario.secoes || []), ...(formulario.perguntas || [])].sort(
    (a, b) => (a.ordem || 0) - (b.ordem || 0)
  );

export const getOrderedBranchingTargets = (formulario) =>
  getOrderedRootItems(formulario).flatMap((item) =>
    item.perguntas
      ? [
          {
            section: item,
            value: getBranchingTargetValue(
              BRANCHING_TARGET_TYPE.SECTION,
              item.guid
            ),
          },
          ...item.perguntas.map((question) => ({
            question,
            value: getBranchingTargetValue(
              BRANCHING_TARGET_TYPE.QUESTION,
              question.guid
            ),
          })),
        ]
      : [
          {
            question: item,
            value: getBranchingTargetValue(
              BRANCHING_TARGET_TYPE.QUESTION,
              item.guid
            ),
          },
        ]
  );

const hasTargetDestination = (option) =>
  [DESTINO_RAMIFICACAO.PERGUNTA, DESTINO_RAMIFICACAO.SECAO].includes(
    Number(option.destinoRamificacao)
  );

export const hasFormularioGlobalBranching = (formulario) =>
  getOrderedBranchingTargets(formulario).some(
    ({ question }) => question && isQuestionBranchingEnabled(question)
  );

export const clearMissingBranchingTargets = (formulario) => {
  const targets = new Set(
    getOrderedBranchingTargets(formulario)
      .map((target) => target.value)
      .filter(Boolean)
  );
  const cleanQuestion = (question) => ({
    ...question,
    opcoes: (question.opcoes || []).map((option) =>
      canConfigureQuestionBranching(question.tipo) &&
      (!hasTargetDestination(option) ||
        targets.has(getBranchingDestinationValue(option)))
        ? option
        : { ...option, destinoRamificacao: null, destinoRamificacaoGuid: null }
    ),
  });

  return {
    ...formulario,
    perguntas: (formulario.perguntas || []).map(cleanQuestion),
    secoes: (formulario.secoes || []).map((section) => ({
      ...section,
      perguntas: (section.perguntas || []).map(cleanQuestion),
    })),
  };
};

export const getQuestionBranchingValidationError = (question, targets) => {
  if (!isQuestionBranchingEnabled(question)) return null;
  const questionGuid = getBranchingGuidKey(question.guid);
  const questionKey = getBranchingQuestionKey(question);
  const currentIndex = targets.findIndex(({ question: targetQuestion }) => {
    if (!targetQuestion) return false;
    if (questionGuid) {
      return getBranchingGuidKey(targetQuestion.guid) === questionGuid;
    }
    return (
      !!questionKey && getBranchingQuestionKey(targetQuestion) === questionKey
    );
  });
  if (currentIndex === -1) return { id: 'msgRamificacaoDestinoInvalido' };
  const nextTargets = new Map(
    targets
      .slice(currentIndex + 1)
      .filter((target) => target.value)
      .map((target) => [target.value, target])
  );

  if (
    (question.opcoes || []).some(
      (option) =>
        hasTargetDestination(option) &&
        !nextTargets.has(getBranchingDestinationValue(option))
    )
  ) {
    return { id: 'msgRamificacaoDestinoInvalido' };
  }

  const emptyDestinationSection = (question.opcoes || [])
    .map(
      (option) => nextTargets.get(getBranchingDestinationValue(option))?.section
    )
    .find((section) => section && !section.perguntas?.length);

  return emptyDestinationSection
    ? {
        id: 'msgRamificacaoSecaoDestinoSemPerguntas',
        toast: true,
        values: { 0: emptyDestinationSection.titulo || '' },
      }
    : null;
};

export const renewBranchingGuids = ({ secoes = [], perguntas = [] }) => {
  const guids = new Map();
  const renew = (guid) => {
    const nextGuid = createGuid();
    if (guid) guids.set(getBranchingGuidKey(guid), nextGuid);
    return nextGuid;
  };
  const renewQuestion = (question) => ({
    ...question,
    guid: renew(question.guid),
  });
  const renewedSecoes = secoes.map((section) => ({
    ...section,
    guid: renew(section.guid),
    perguntas: (section.perguntas || []).map(renewQuestion),
  }));
  const renewedPerguntas = perguntas.map(renewQuestion);
  const remapQuestion = (question) => ({
    ...question,
    opcoes: (question.opcoes || []).map((option) => {
      const nextGuid = guids.get(
        getBranchingGuidKey(option.destinoRamificacaoGuid)
      );
      return nextGuid
        ? { ...option, destinoRamificacaoGuid: nextGuid }
        : option;
    }),
  });

  return {
    secoes: renewedSecoes.map((section) => ({
      ...section,
      perguntas: section.perguntas.map(remapQuestion),
    })),
    perguntas: renewedPerguntas.map(remapQuestion),
  };
};

const resetCopiedItems = (items = []) =>
  items.map((item) => ({ ...item, id: 0, [CLIENT_ID_FIELD]: undefined }));

const resetCopiedQuestion = (question) => ({
  ...question,
  id: 0,
  [CLIENT_ID_FIELD]: undefined,
  opcoes: resetCopiedItems(question.opcoes),
  ...(question.linhas && { linhas: resetCopiedItems(question.linhas) }),
  ...(question.colunas && { colunas: resetCopiedItems(question.colunas) }),
});

export const copyFormularioStructure = (formulario) =>
  renewBranchingGuids({
    secoes: (formulario.secoes || []).map((section) => ({
      ...section,
      id: 0,
      [CLIENT_ID_FIELD]: undefined,
      perguntas: (section.perguntas || []).map(resetCopiedQuestion),
    })),
    perguntas: (formulario.perguntas || []).map(resetCopiedQuestion),
  });

export const getFormularioGlobalBranchingSections = (
  sections,
  answers,
  randomizarPerguntas = false
) => {
  if (randomizarPerguntas) return sections;
  const questions = sections.flatMap((section) => section.perguntas);
  const indexes = new Map();
  let sectionStart = 0;

  sections.forEach((section) => {
    if (section.guid) {
      indexes.set(
        getBranchingTargetValue(BRANCHING_TARGET_TYPE.SECTION, section.guid),
        sectionStart
      );
    }
    section.perguntas.forEach((question, index) => {
      if (question.guid) {
        indexes.set(
          getBranchingTargetValue(
            BRANCHING_TARGET_TYPE.QUESTION,
            question.guid
          ),
          sectionStart + index
        );
      }
    });
    sectionStart += section.perguntas.length;
  });

  const visited = new Set();

  for (let index = 0; index < questions.length; ) {
    const question = questions[index];
    visited.add(question);
    let nextIndex = index + 1;
    if (isQuestionBranchingEnabled(question)) {
      const value = answers[question._formularioGlobalPreviewKey]?.valor;
      const option =
        value != null && !Array.isArray(value)
          ? (question.opcoes || []).find(
              (item) => String(item.valor) === String(value)
            )
          : null;
      const destination = getBranchingDestinationValue(option);
      if (destination === BRANCHING_FINISH_VALUE) break;
      const targetIndex = indexes.get(destination);
      if (targetIndex > index) nextIndex = targetIndex;
    }
    index = nextIndex;
  }

  if (visited.size === questions.length) return sections;
  return sections
    .map((section) => ({
      ...section,
      perguntas: section.perguntas.filter((question) => visited.has(question)),
    }))
    .filter((section) => section.perguntas.length > 0);
};

export const keepFormularioGlobalReachableAnswers = (sections, answers) => {
  const keys = new Set(
    sections.flatMap((section) =>
      section.perguntas.map((question) => question._formularioGlobalPreviewKey)
    )
  );
  return Object.fromEntries(
    Object.entries(answers).filter(([key]) => keys.has(key))
  );
};
